import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAdminEditions,
  getAdminCourses,
  createEdition,
  updateEdition,
  syncZoom,
  exportEnrollments,
  sendReminders,
} from '../../api/courses';
import type { CourseEdition, Course } from '../../types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface EditionFormData {
  courseId: string;
  label: string;
  zoomMeetingId: string;
  zoomMeetingUrl: string;
  scheduledAt: string;
  durationMinutes: number;
  maxParticipants: number;
  registrationOpen: boolean;
  inPresenza: boolean;
  waitlistEnabled: boolean;
}

function EditionModal({
  edition,
  courses,
  onClose,
  onSaved,
}: {
  edition?: CourseEdition;
  courses: Course[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!edition;

  const defaultScheduled = edition?.scheduledAt
    ? (() => { const d = new Date(edition.scheduledAt); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); })()
    : '';

  const [form, setForm] = useState<EditionFormData>({
    courseId: edition?.courseId ?? '',
    label: edition?.label ?? '',
    zoomMeetingId: edition?.zoomMeetingId ?? '',
    zoomMeetingUrl: edition?.zoomMeetingUrl ?? '',
    scheduledAt: defaultScheduled,
    durationMinutes: edition?.durationMinutes ?? 60,
    maxParticipants: edition?.maxParticipants ?? 50,
    registrationOpen: edition?.registrationOpen ?? true,
    inPresenza: edition?.inPresenza ?? false,
    waitlistEnabled: edition?.waitlistEnabled ?? true,
  });

  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setForm((f) => ({
      ...f,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : type === 'number'
          ? parseInt(value, 10)
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      const payload = {
        ...form,
        scheduledAt: new Date(form.scheduledAt).toISOString(),
        zoomMeetingId: form.zoomMeetingId || undefined,
        zoomMeetingUrl: form.zoomMeetingUrl || undefined,
      };

      if (isEdit) {
        await updateEdition(edition.id, payload);
      } else {
        await createEdition(payload);
      }

      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <h2 className="modal-title">
          {isEdit ? 'Modifica edizione' : 'Nuova edizione'}
        </h2>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">
              Corso <span className="required">*</span>
            </label>
            <select
              name="courseId"
              className="form-control"
              value={form.courseId}
              onChange={handleChange}
              required
            >
              <option value="">-- Seleziona un corso --</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              Label / Nome edizione <span className="required">*</span>
            </label>
            <input
              type="text"
              name="label"
              className="form-control"
              value={form.label}
              onChange={handleChange}
              placeholder="es. Sessione primavera 2024"
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">
                Data e ora <span className="required">*</span>
              </label>
              <input
                type="datetime-local"
                name="scheduledAt"
                className="form-control"
                value={form.scheduledAt}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Durata (minuti)</label>
              <input
                type="number"
                name="durationMinutes"
                className="form-control"
                value={form.durationMinutes}
                onChange={handleChange}
                min={15}
                max={480}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Max partecipanti</label>
            <input
              type="number"
              name="maxParticipants"
              className="form-control"
              value={form.maxParticipants}
              onChange={handleChange}
              min={1}
              max={1000}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Zoom Meeting ID</label>
            <input
              type="text"
              name="zoomMeetingId"
              className="form-control"
              value={form.zoomMeetingId}
              onChange={handleChange}
              placeholder="123456789"
            />
            <span className="form-hint">
              L'ID numerico del meeting Zoom (trovato nell'URL del meeting)
            </span>
          </div>

          <div className="form-group">
            <label className="form-label">Zoom Meeting URL</label>
            <input
              type="url"
              name="zoomMeetingUrl"
              className="form-control"
              value={form.zoomMeetingUrl}
              onChange={handleChange}
              placeholder="https://zoom.us/j/123456789"
            />
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                id="registrationOpen"
                name="registrationOpen"
                className="form-check-input"
                checked={form.registrationOpen}
                onChange={handleChange}
              />
              <label htmlFor="registrationOpen" className="form-check-label">
                Registrazioni aperte (visibile nel form pubblico)
              </label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                id="inPresenza"
                name="inPresenza"
                className="form-check-input"
                checked={form.inPresenza}
                onChange={handleChange}
              />
              <label htmlFor="inPresenza" className="form-check-label">
                📍 In presenza (mostra badge arancione nel form pubblico)
              </label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                id="waitlistEnabled"
                name="waitlistEnabled"
                className="form-check-input"
                checked={form.waitlistEnabled}
                onChange={handleChange}
              />
              <label htmlFor="waitlistEnabled" className="form-check-label">
                Lista d'attesa abilitata (se disabilitata, a posti esauriti la giornata risulta non selezionabile)
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Annulla
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSaving}>
              {isSaving ? 'Salvataggio...' : isEdit ? 'Salva modifiche' : 'Crea edizione'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminEditionsPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingEdition, setEditingEdition] = useState<CourseEdition | undefined>();
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const { data: editions = [], isLoading } = useQuery({
    queryKey: ['admin-editions'],
    queryFn: getAdminEditions,
  });

  const { data: courses = [] } = useQuery({
    queryKey: ['admin-courses'],
    queryFn: getAdminCourses,
  });

  const handleSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-editions'] });
  };

  const handleSendReminders = async (edition: CourseEdition) => {
    setSendingReminderId(edition.id);
    setSyncMessage(null);
    try {
      const result = await sendReminders(edition.id);
      setSyncMessage(result.queued > 0
        ? `Promemoria inviati a ${result.queued} iscritti.`
        : 'Nessun iscritto da notificare (tutti già ricevuto o nessun iscritto attivo).'
      );
    } catch (err: any) {
      setSyncMessage(err?.response?.data?.error ?? 'Errore nell\'invio dei promemoria');
    } finally {
      setSendingReminderId(null);
    }
  };

  const handleSyncZoom = async (edition: CourseEdition) => {
    setSyncingId(edition.id);
    setSyncMessage(null);
    try {
      const result = await syncZoom(edition.id);
      setSyncMessage(
        `Sync completato: ${result.zoomRegistrantCount} registranti Zoom, ${result.syncResults.updated} aggiornati`
      );
      queryClient.invalidateQueries({ queryKey: ['admin-editions'] });
    } catch (err: any) {
      setSyncMessage(err?.response?.data?.error ?? 'Errore nella sincronizzazione');
    } finally {
      setSyncingId(null);
    }
  };

  const sortedEditions = [...editions].sort(
    (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Edizioni</h1>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditingEdition(undefined);
            setShowModal(true);
          }}
        >
          + Nuova edizione
        </button>
      </div>

      {syncMessage && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          {syncMessage}
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner" />
          </div>
        ) : editions.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray-500)' }}>
            Nessuna edizione. Crea la prima edizione!
          </div>
        ) : (
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Corso</th>
                  <th>Edizione</th>
                  <th>Data</th>
                  <th>Posti</th>
                  <th>Zoom</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {sortedEditions.map((edition) => (
                  <tr key={edition.id}>
                    <td style={{ fontWeight: 500 }}>
                      {edition.course?.name ?? '—'}
                    </td>
                    <td style={{ color: 'var(--gray-600)' }}>{edition.label}</td>
                    <td style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                      {format(new Date(edition.scheduledAt), "d MMM yyyy HH:mm", { locale: it })}
                    </td>
                    <td>
                      <span
                        style={{
                          fontWeight: 600,
                          color:
                            (edition.enrollmentCount ?? 0) >= edition.maxParticipants
                              ? 'var(--danger)'
                              : 'var(--gray-700)',
                        }}
                      >
                        {edition.enrollmentCount ?? 0}
                      </span>
                      <span style={{ color: 'var(--gray-400)' }}>
                        /{edition.maxParticipants}
                      </span>
                    </td>
                    <td>
                      {edition.zoomMeetingId ? (
                        <span className="badge badge-green">
                          {edition.zoomMeetingId}
                        </span>
                      ) : (
                        <span className="badge badge-gray">Non configurato</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          edition.registrationOpen ? 'badge-green' : 'badge-gray'
                        }`}
                      >
                        {edition.registrationOpen ? 'Aperta' : 'Chiusa'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setEditingEdition(edition);
                            setShowModal(true);
                          }}
                        >
                          Modifica
                        </button>
                        <Link
                          to={`/admin/editions/${edition.id}/content`}
                          className="btn btn-secondary btn-sm"
                        >
                          📁 Contenuti
                        </Link>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => exportEnrollments(edition.id).catch(() => alert('Errore durante l\'esportazione'))}
                          title="Esporta iscritti CSV"
                        >
                          ⬇ CSV
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleSendReminders(edition)}
                          disabled={sendingReminderId === edition.id}
                          title="Invia promemoria giorno prima a tutti gli iscritti attivi"
                        >
                          {sendingReminderId === edition.id ? 'Invio...' : '📧 Promemoria'}
                        </button>
                        {edition.zoomMeetingId && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleSyncZoom(edition)}
                            disabled={syncingId === edition.id}
                          >
                            {syncingId === edition.id ? 'Sync...' : 'Sync Zoom'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <EditionModal
          edition={editingEdition}
          courses={courses}
          onClose={() => setShowModal(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
